<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import closeIcon from 'dashboard/assets/real-estate/broker-onboarding/close.svg';
import firstImage from 'dashboard/assets/real-estate/broker-onboarding/primeira.png';
import secondImage from 'dashboard/assets/real-estate/broker-onboarding/segunda.png';
import thirdImage from 'dashboard/assets/real-estate/broker-onboarding/terceira.png';

const emit = defineEmits(['complete']);
const { t } = useI18n();
const activeStep = ref(0);
const touchStartX = ref(null);
const steps = [
  {
    titleKey: 'REAL_ESTATE_BROKER_DASHBOARD.ONBOARDING.COMMISSIONS',
    image: firstImage,
    background: 'bg-[#EDF8F7]',
  },
  {
    titleKey: 'REAL_ESTATE_BROKER_DASHBOARD.ONBOARDING.BALANCE',
    image: secondImage,
    background: 'bg-[#FFF1E5]',
  },
  {
    titleKey: 'REAL_ESTATE_BROKER_DASHBOARD.ONBOARDING.TAX_DOCUMENTS',
    image: thirdImage,
    background: 'bg-[#FFF7E5]',
  },
];
const currentStep = computed(() => steps[activeStep.value]);
const isLastStep = computed(() => activeStep.value === steps.length - 1);
const currentTitle = computed(() => {
  if (activeStep.value === 0)
    return t('REAL_ESTATE_BROKER_DASHBOARD.ONBOARDING.COMMISSIONS');
  if (activeStep.value === 1)
    return t('REAL_ESTATE_BROKER_DASHBOARD.ONBOARDING.BALANCE');
  return t('REAL_ESTATE_BROKER_DASHBOARD.ONBOARDING.TAX_DOCUMENTS');
});
const primaryLabel = computed(() => {
  if (isLastStep.value)
    return t('REAL_ESTATE_BROKER_DASHBOARD.ONBOARDING.START');
  return t('REAL_ESTATE_BROKER_DASHBOARD.ONBOARDING.NEXT');
});

const selectStep = index => {
  activeStep.value = Math.min(Math.max(index, 0), steps.length - 1);
};
const advance = () =>
  isLastStep.value ? emit('complete') : selectStep(activeStep.value + 1);
const handleTouchStart = event => {
  touchStartX.value = event.changedTouches[0]?.clientX ?? null;
};
const handleTouchEnd = event => {
  if (touchStartX.value === null) return;

  const distance = (event.changedTouches[0]?.clientX ?? 0) - touchStartX.value;
  touchStartX.value = null;
  if (Math.abs(distance) < 40) return;
  selectStep(activeStep.value + (distance < 0 ? 1 : -1));
};
</script>

<template>
  <section
    role="dialog"
    aria-modal="true"
    :aria-label="t('REAL_ESTATE_BROKER_DASHBOARD.ONBOARDING.ARIA')"
    data-test="real-estate-broker-onboarding"
    class="fixed inset-0 z-lais-max flex min-h-dvh flex-col gap-[34px] overflow-y-auto bg-white p-6 md:hidden"
  >
    <div class="flex w-full justify-end">
      <button
        type="button"
        class="grid size-8 place-items-center rounded-full focus-visible:outline-none focus-visible:shadow-lais-focus"
        :aria-label="t('REAL_ESTATE_BROKER_DASHBOARD.ONBOARDING.CLOSE')"
        data-test="broker-onboarding-close"
        @click="emit('complete')"
      >
        <img :src="closeIcon" alt="" class="size-[17px]" />
      </button>
    </div>

    <div
      data-test="broker-onboarding-slide"
      class="flex flex-col gap-[34px]"
      @touchstart.passive="handleTouchStart"
      @touchend="handleTouchEnd"
    >
      <div
        class="flex h-[347px] w-full items-center justify-center rounded-[10px]"
        :class="currentStep.background"
      >
        <img
          :src="currentStep.image"
          :alt="currentTitle"
          class="max-h-full max-w-full object-contain"
          data-test="broker-onboarding-image"
        />
      </div>
      <h2
        class="text-center font-laisDisplay text-2xl font-bold leading-[30px] text-[#FF7A11]"
      >
        {{ currentTitle }}
      </h2>
    </div>

    <div class="flex items-center justify-center gap-2">
      <button
        v-for="(step, index) in steps"
        :key="step.titleKey"
        type="button"
        class="!min-h-0 !min-w-0 !border-0 !p-0 rounded-full bg-[#85E0D9] transition-all"
        :class="index === activeStep ? '!size-3 bg-[#01988C]' : '!size-2'"
        :aria-current="index === activeStep ? 'step' : undefined"
        :aria-label="
          t('REAL_ESTATE_BROKER_DASHBOARD.ONBOARDING.GO_TO_STEP', {
            step: index + 1,
          })
        "
        data-test="broker-onboarding-dot"
        @click="selectStep(index)"
      />
    </div>

    <div class="space-y-2">
      <button
        type="button"
        class="flex min-h-[54px] w-full items-center justify-center gap-2 rounded-[10px] border border-[#01988C] bg-[#01988C] px-6 py-[15px] text-base font-bold text-white hover:bg-[#01655D] focus-visible:outline-none focus-visible:shadow-lais-focus"
        data-test="broker-onboarding-primary"
        @click="advance"
      >
        {{ primaryLabel }}
        <span v-if="!isLastStep" class="i-lucide-arrow-right size-5" />
      </button>
      <button
        type="button"
        class="min-h-12 w-full rounded-[10px] px-6 py-3 text-base font-bold text-[#01988C] hover:bg-[#F0F3F5] focus-visible:outline-none focus-visible:shadow-lais-focus"
        data-test="broker-onboarding-skip"
        @click="emit('complete')"
      >
        {{ t('REAL_ESTATE_BROKER_DASHBOARD.ONBOARDING.SKIP') }}
      </button>
    </div>
  </section>
</template>
